/* CreateEditCommandComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const _ = require("lodash");
const Renderer = require("./CreateEditCommandRenderer");
/**
 * @class CreateEditCommandComponent
 * @extends EventEmitter
 *
 * @fires CreateEditCommandComponent#Data
 * @fires CreateEditCommandComponent#Error
 * @fires CreateEditCommandComponent#Shutdown
 */
class CreateEditCommandComponent extends EventEmitter {
    constructor(data) {
        super();
        this._data = data;
        this._commandSetEntityDef = this._data.command;
        this._activeOrgId = this._data.orgId;
        this._ConstructInstanceEntity = this._data.ConstructInstance;
        this._commandSetEntity = this._data.commandEntity || {};
        this._redactionRegex = this._data.redactionRegex;
        this._valueMapEntityDef = this._data.valueMap;
        this._SiteEntityClass = this._data.siteEntity;
        this._typeBaseMap = this._data.typeBaseMap;
        this._logger = this._data.logger;
        this._CustomFieldsEntityClass = this._data.customFieldEntityClass;
        this._customFieldsWithInherited = {};
        this._sitePaths = [];
        this._parentView = this._data.parentView;
        this._vendorOsMap = {};
        this.conceptItems = [];
        this._vendorMap = this._data.vendorMap;
        this.rsiItems = [];
        this._rsiItemsForPickList = [];
        this.closePopUp = _.debounce(this.closePopUp, 100);
        this._registerListeners();
        this._renderer = new Renderer(this);
        this._redactionExpItemsForPickList = [];
        this._selectedRedactions = [];
    }

    get renderer() {
        return this.getRenderer();
    }

    get rootElement() {
        return this._renderer.getRootElement();
    }

    get activeOrgId() {
        return this._activeOrgId;
    }

    get userFeedback() {
        return this._userFeedback;
    }

    // prettier-ignore
    set "$teve.ui.context"(context) { //jshint ignore:line
        this._context = context;
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {}
    _unregisterListeners() {}
    _shutdown() {
        this.emit("Shutdown");
        this._unregisterListeners();
        this._commandSetEntityInst.clearPendingPatches();
        this._commandSetEntityInst.clearPendingRelationships();
        return Promise.all([this._renderer.shutdown()])
            .then(() => {
                this._renderer = undefined;
                this._shutdownProm = undefined;
                this._redactionExpItemsForPickList = undefined;
                this._selectedRedactions = undefined;
                this._data = undefined;
                this._commandSetEntityDef = undefined;
                this._activeOrgId = undefined;
                this._ConstructInstanceEntity = undefined;
                this._commandSetEntity = undefined;
                this._redactionRegex = undefined;
                this.conceptItems = undefined;
                this._vendorMap = undefined;
                this.rsiItems = undefined;
                this._rsiItemsForPickList = undefined;
                this.removeAllListeners();
            })
            .catch((error) => {
                console.log(`${this.constructor.name}: error in shutdown`);
                console.error(error);
            });
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    whenModelIsReady() {
        this._commandSetEntityInst = _.isEmpty(this._commandSetEntity)
            ? new this._commandSetEntityDef(this.activeOrgId)
            : this._commandSetEntity;
        return Promise.all([
            this._loadAllSites(),
            this._getConceptItems(),
            this._getRsiList(),
            this._getRedactionExpressions(),
            this._getVendorOsMap(),
            this.retrieveCustomFieldsEntity(this.activeOrgId),
        ]);
    }

    getRenderer() {
        return this._renderer;
    }

    shutdown() {
        if (this._shutdownProm === null) {
            return Promise.resolve();
        }
        this._shutdownProm = this._shutdownProm || this._shutdown();
        return this._shutdownProm;
    }

    closePopUp() {
        this.rejectPopUp({userRejected: true});
    }

    async saveAndClosePopUp(force = false) {
        let errored = false;
        try {
            if (this._commandSetEntityInst.isSynchronized()) {
                if (force) {
                    await this._commandSetEntityInst.update({skipHooks: ["auditPolicy"]});
                } else {
                    await this._commandSetEntityInst.update();
                }
            } else {
                await this._commandSetEntityInst.create();
            }
        } catch (err) {
            errored = true;
            let msg = err.errorMsg;
            let forceable = false;
            if (err.errorCode === "EntityAlreadyExists") {
                msg = msg
                    .replace("CommandSet", "Command Set")
                    .replace("named ", 'named "')
                    .replace(" already", '" already');
            } else if (err.errorCode === "ValidationWarning") {
                forceable = true;
            }
            this.emit("Error", msg, forceable);
        } finally {
            if (!errored) {
                this._parentView._getAllCommandSets();
                this.closePopUp();
            }
        }
    }

    resolvePopup(data) {
        this._context.resolve(data);
    }

    rejectPopUp(data) {
        this._context.reject(data);
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/
    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/
    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    async _loadAllSites() {
        const sitePaths = await this._SiteEntityClass.loadSites(
            this.activeOrgId,
            {},
            {
                id: true,
                path: true,
                name: true,
                locationType: true,
            },
            {
                path: 1,
            }
        );
        this._sitePaths = sitePaths;
        return Promise.resolve();
    }

    _getConceptItems() {
        return this._ConstructInstanceEntity
            .loadConceptItemLabelValues(this.activeOrgId)
            .then((conceptItems) => {
                this.conceptItems = conceptItems.reduce((result, item) => {
                    result[item.value] = item;
                    return result;
                }, {});
                this._conceptItemsForPickList = Object.values(this.conceptItems).map((i) => ({
                    label: i.label,
                    value: i.value,
                }));
                return Promise.resolve();
            });
    }

    _getRsiList() {
        return this._ConstructInstanceEntity
            .loadRsiLabelValues(this.activeOrgId, {"Config Modeling Only": {$ne: true}})
            .then((rsiItems) => {
                this.rsiItems = rsiItems.reduce((result, item) => {
                    result[item.value] = item;
                    return result;
                }, {});
                this._rsiItemsForPickList = Object.values(this.rsiItems).map((i) => ({
                    label: i.label,
                    value: i.value,
                }));
                return Promise.resolve();
            });
    }

    _getRedactionExpressions() {
        return this._redactionRegex
            .loadRedactionRegexes(this.activeOrgId, {}, {id: true, name: true}, {name: 1})
            .then((result) => {
                if (_.isArray(result)) {
                    this._redactionExpItemsForPickList = result.map((i) => ({
                        label: i.getName(),
                        value: i.getId(),
                    }));
                }
                if (this._commandSetEntityInst.getId()) {
                    // Has CommandSetEntity just been added?
                    return this._commandSetEntityInst.loadRelatedRedactionRegexes(
                        {},
                        {id: true, name: true},
                        {name: 1}
                    );
                } else {
                    return Promise.resolve([]);
                }
            })
            .then((result) => {
                if (_.isArray(result)) {
                    this._selectedRedactions = result.map((i) => i.getId());
                }
            });
    }

    async _getVendorOsMap() {
        this._vendorOsMap = await this._valueMapEntityDef.loadVendorOsMapEntity(this.activeOrgId);
        this._vendorOsMap = (this._vendorOsMap && this._vendorOsMap.getValueMap()) || {};

        Object.keys(this._vendorMap).map((i) => {
            if (!this._vendorOsMap[i] || _.isEmpty(this._vendorOsMap[i].os)) {
                delete this._vendorMap[i];
            }
        });

        return Promise.resolve();
    }

    setCustomFieldsEntity(newOrgInstance) {
        this._customFieldsEntityInstance = newOrgInstance;
        return this._customFieldsEntityInstance;
    }

    retrieveCustomFieldsEntity(tenantId) {
        if (this._customFieldsEntityInstance && this._customFieldsEntityInstance.shutdown) {
            this._customFieldsEntityInstance
                .shutdown()
                .catch((error) => this._logger.warn({error}));
        }
        return this._CustomFieldsEntityClass
            .retrieveCustomFields(tenantId)
            .then((customFieldsEntity) => {
                this.setCustomFieldsEntity(customFieldsEntity);
                return customFieldsEntity.loadInheritedFields();
            })
            .then((inheritedFields) => {
                Object.assign(
                    this._customFieldsWithInherited,
                    this._customFieldsEntityInstance.fields,
                    inheritedFields
                );
                return this._customFieldsWithInherited;
            });
    }

    _filterOs(vendor) {
        return (
            (_.get(this._vendorOsMap, vendor + ".os") &&
                Object.entries(this._vendorOsMap[vendor].os).map(([key, value]) => ({
                    label: _.isObject(value) ? value.displayName : value,
                    value: key,
                }))) ||
            []
        );
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = CreateEditCommandComponent;
